// Bookers marketing site — EN / FR UI copy and CMS fallbacks.

export const LOCALES = [
  { code: "en", label: "EN", name: "English" },
  { code: "fr", label: "FR", name: "Français" },
];

const en = {
  nav: {
    features: "Product",
    solutions: "Solutions",
    pricing: "Pricing",
    contact: "Contact",
    login: "Log in",
    signUp: "Sign Up",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    changeLanguage: "Change language",
  },
  hero: {
    eyebrow: "Simplify. Manage. Grow.",
    title: "The smart reservation system for hospitality",
    description:
      "Manage tables, experiences, and events effortlessly — all in one seamless platform designed to enhance guest experiences and boost efficiency.",
    ctaPrimary: "Sign Up Free",
    ctaSecondary: "Explore Product",
    badgeRating: "4.9 of 5",
    trustEasy: "Easy Integration",
    trustMobile: "Mobile Ready",
    trustFast: "Fast Setup",
    cardEyebrow: "New reservation",
    guests: "Guests",
    guestsValue: "4 people",
    date: "Date",
    dateValue: "Fri, 8:00 PM",
    table: "Table",
    tableValue: "Window · T12",
    confirmed: "Confirmed ✓",
    imageAlt: "Modern restaurant interior",
  },
  services: {
    eyebrow: "Services",
    title: "What we offer",
    description: "Everything you need to run reservations, experiences and events — beautifully connected.",
    getStarted: "Get Started",
  },
  features: {
    eyebrow: "Product",
    title: "Powerful tools for smooth restaurant operations",
    description:
      "From online bookings to guest management, discover all the tools that make running your restaurant easier.",
  },
  flexibility: {
    eyebrow: "Built your way",
    title: "Activate only what you need",
    description:
      "Run tables, experiences, or events — on their own or all together. You can even use Bookers for events only. Flip a module on whenever you are ready.",
    modularNote: "Need events only? No problem — Bookers is fully modular.",
    paymentEyebrow: "Payment protection",
    paymentTitle: "Get paid, and cut no-shows",
    setupPayments: "Set up payments",
  },
  howItWorks: {
    eyebrow: "How it works",
    title: "Start receiving reservations in minutes",
    description: "Sign up, download the app, and share your own booking page. It really is that simple.",
    downloadApp: "Download the Bookers owner app",
    appStoreTop: "Download on the",
    appStoreBottom: "App Store",
    playStoreTop: "Get it on",
    playStoreBottom: "Google Play",
    signUpFree: "Sign Up Free",
  },
  results: {
    eyebrow: "Why Bookers?",
    title: "Proven results for restaurants like yours",
    description:
      "Trusted by hundreds of restaurants worldwide, Bookers delivers measurable results that transform your front-of-house operations.",
  },
  testimonials: {
    eyebrow: "Testimonials",
    title: "What our partners say",
  },
  stats: {
    marquee: "Trusted by leading restaurants and hotels",
  },
  support: {
    talkToTeam: "Talk to our team",
  },
  faq: {
    title: "Frequently asked questions",
    description: "Everything you need to know about Bookers. Still have questions? Reach out anytime.",
  },
  cta: {
    eyebrow: "Get started",
    title: "Ready to fill more tables?",
    description:
      "We are here to help you streamline your restaurant reservations and guest management — get set up in minutes.",
    signUpFree: "Sign Up Free",
    bookDemo: "Book a demo",
    footnote:
      "No credit card required. Free up to 150 reservations / month for new venues. Pay only when you grow.",
  },
  freeTrial: {
    noCard: "No credit card. Pay only when you grow.",
  },
  pricing: {
    eyebrow: "Pricing Plans",
    title: "Simple, transparent pricing",
    description: "Introduction pricing — pick the package that fits your venue. No hidden fees, cancel anytime.",
    footer:
      "Introduction pricing, billed monthly. VAT excluded. Need events only or a custom setup? Bookers is modular — just reach out.",
    perMonth: "/ month",
    signUp: "Sign Up",
  },
  solutions: {
    eyebrow: "Solutions",
    title: "Smart reservation solutions for hospitality",
    description:
      "Empower your restaurant and hotel operations with seamless booking management, personalized service, and powerful automation.",
  },
  problemSolution: {
    title: "We have you covered, whatever venue you run",
    problemLabel: "The problem today",
    solutionLabel: "With Bookers",
    learnMore: "Learn more",
  },
  footer: {
    tagline: "Smart reservation system for restaurants & hospitality. Simplify. Manage. Grow.",
    product: "Product",
    company: "Company",
    resources: "Resources",
    features: "Product",
    solutions: "Solutions",
    pricing: "Pricing",
    integrations: "Integrations",
    ownerApp: "Owner App",
    about: "About us",
    blog: "Blog",
    contact: "Contact",
    bookDemo: "Book a demo",
    partners: "Partners",
    helpCenter: "Help Center",
    apiDocs: "API Docs",
    status: "Status",
    community: "Community",
    webinars: "Webinars",
    rights: "© 2026 Bookers. All rights reserved.",
    terms: "Terms",
    privacy: "Privacy",
    cookies: "Cookies",
    madeFor: "Made for hospitality",
  },
  contact: {
    eyebrow: "Contact",
    title: "Let's talk about your project",
    subtitle: "Fill out the form below and our team will get back to you as soon as possible.",
    blurb: "Simplify reservation management with our smart system for restaurants and hospitality.",
    address: "Address",
    addressValue: "Neuchâtel, Switzerland",
    phone: "Phone",
    email: "Email",
    supportHours: "Support hours",
    supportLine: "24/7 — We speak FR, DE, IT, EN",
    lastName: "Last name",
    firstName: "First name",
    restaurant: "Restaurant name",
    message: "Message",
    required: "Required fields",
    placeholderLast: "Dupont",
    placeholderFirst: "Marie",
    placeholderRestaurant: "Le Petit Bistro",
    placeholderPhone: "+41 00 000 00 00",
    placeholderMessage: "How can we help?",
    submit: "Send message",
    sending: "Sending...",
    successTitle: "Message sent!",
    successBody: "Thanks {name}, our team will get back to you shortly.",
    backHome: "Back to home",
    toastRequired: "Please fill in the required fields.",
    toastSuccess: "Message sent successfully!",
    toastError: "Could not send. Please try again.",
  },
  signup: {
    panelTitle: "Simplify your reservations, starting today.",
    panelSub: "Join hundreds of restaurants using Bookers to fill their tables.",
    rated: "Rated 4.9/5 by our partners",
    imageAlt: "Restaurant interior",
    eyebrow: "Book a demo",
    title: "Get started with Bookers",
    subtitle: "Fill out the form and our team will contact you to schedule a personalized demo.",
    restaurant: "Restaurant name",
    lastName: "Last name",
    firstName: "First name",
    phone: "Phone",
    address: "Address",
    message: "Message",
    placeholderRestaurant: "Le Petit Bistro",
    placeholderLast: "Dupont",
    placeholderFirst: "Marie",
    placeholderPhone: "+41 00 000 00 00",
    placeholderAddress: "Rue de la Gare 12, 2000 Neuchâtel",
    placeholderMessage: "Tell us about your venue...",
    consentPrefix:
      "By submitting this form, you agree that Bookers may process your data for legitimate interest and send you marketing communications. You can unsubscribe at any time. All emails include an unsubscribe link. See our ",
    privacyLink: "privacy policy",
    consentSuffix: ".",
    submit: "Book a demo",
    sending: "Sending...",
    footnote: "Free up to 150 reservations / month. No credit card required.",
    successTitle: "Request sent!",
    successBody: "Thanks {name}, our team will contact you soon to schedule your demo.",
    backHome: "Back to home",
    toastRequired: "Please fill in all required fields.",
    toastConsent: "Please accept the terms.",
    toastSuccess: "Demo request sent!",
    toastError: "Could not send. Please try again.",
  },
  legal: {
    back: "← Back",
    loading: "Loading...",
    empty: "Content coming soon.",
    contactLine: "For questions, contact us at",
    termsTitle: "Terms of use",
    privacyTitle: "Privacy policy",
    cookiesTitle: "Cookie policy",
    lastUpdated: "Last updated: {date}",
  },
  floating: {
    aria: "Request a callback",
    title: "Request a callback",
    subtitle: "Leave your details and we'll call you back quickly.",
    namePlaceholder: "Your name",
    submit: "Request callback",
    successTitle: "Request sent!",
    successBody: "We'll call you back soon.",
    close: "Close",
    toastRequired: "Please fill in both fields.",
    toastSuccess: "We'll call you back soon!",
    toastError: "Error. Please try again.",
  },
  common: {
    chf: "CHF",
  },
  errors: {
    generic: "Something went wrong. Please try again.",
    not_authenticated: "Not authenticated",
    invalid_token_type: "Invalid token type",
    user_not_found: "User not found",
    admin_required: "Admin access required",
    token_expired: "Session expired. Please sign in again.",
    invalid_token: "Invalid session",
    invalid_email_or_password: "Invalid email or password",
    current_password_incorrect: "Current password is incorrect",
    password_must_differ: "New password must be different from the current password",
    password_too_short: "Password must be at least {min} characters",
    lead_not_found: "Lead not found",
    login_failed: "Login failed",
  },
  admin: {
    title: "Admin Panel",
    loginSubtitle: "Sign in to manage your site",
    email: "Email",
    password: "Password",
    signIn: "Sign in",
    save: "Save",
    logout: "Log out",
    leads: "Leads",
    cms: "CMS",
    passwordTab: "Password",
    statusUpdated: "Status updated",
    leadDeleted: "Lead deleted",
    deleteConfirm: "Delete this lead?",
    error: "Error",
    noLeads: "No leads",
    total: "Total",
    newLeads: "New",
    contacts: "Contacts",
    demos: "Demos",
    callbacks: "Callbacks",
    filterAll: "All",
    sectionSaved: "Section saved!",
    saveError: "Save failed",
    passwordUpdated: "Password updated",
  },
};

const fr = {
  nav: {
    features: "Produit",
    solutions: "Solutions",
    pricing: "Tarifs",
    contact: "Contact",
    login: "Connexion",
    signUp: "Inscription",
    openMenu: "Ouvrir le menu",
    closeMenu: "Fermer le menu",
    changeLanguage: "Changer de langue",
  },
  hero: {
    eyebrow: "Simplifiez. Gérez. Grandissez.",
    title: "Le système de réservation intelligent pour l'hôtellerie-restauration",
    description:
      "Gérez tables, expériences et événements sans effort — une plateforme fluide pour améliorer l'expérience client et gagner en efficacité.",
    ctaPrimary: "Inscription gratuite",
    ctaSecondary: "Découvrir le produit",
    badgeRating: "4,9 sur 5",
    trustEasy: "Intégration facile",
    trustMobile: "Prêt mobile",
    trustFast: "Mise en place rapide",
    cardEyebrow: "Nouvelle réservation",
    guests: "Convives",
    guestsValue: "4 personnes",
    date: "Date",
    dateValue: "Ven, 20h00",
    table: "Table",
    tableValue: "Fenêtre · T12",
    confirmed: "Confirmé ✓",
    imageAlt: "Intérieur de restaurant moderne",
  },
  services: {
    eyebrow: "Services",
    title: "Ce que nous proposons",
    description:
      "Tout pour gérer réservations, expériences et événements — parfaitement connectés.",
    getStarted: "Commencer",
  },
  features: {
    eyebrow: "Produit",
    title: "Des outils puissants pour des opérations fluides",
    description:
      "De la réservation en ligne à la gestion des clients, découvrez les outils qui simplifient votre restaurant.",
  },
  flexibility: {
    eyebrow: "À votre façon",
    title: "Activez uniquement ce dont vous avez besoin",
    description:
      "Tables, expériences ou événements — seuls ou ensemble. Bookers peut aussi servir uniquement pour les événements. Activez un module quand vous êtes prêt.",
    modularNote: "Événements uniquement ? Aucun problème — Bookers est entièrement modulaire.",
    paymentEyebrow: "Protection des paiements",
    paymentTitle: "Encaissez et réduisez les no-shows",
    setupPayments: "Configurer les paiements",
  },
  howItWorks: {
    eyebrow: "Comment ça marche",
    title: "Recevez des réservations en quelques minutes",
    description:
      "Inscrivez-vous, téléchargez l'app et partagez votre page de réservation. C'est aussi simple.",
    downloadApp: "Téléchargez l'app propriétaire Bookers",
    appStoreTop: "Télécharger sur",
    appStoreBottom: "App Store",
    playStoreTop: "Disponible sur",
    playStoreBottom: "Google Play",
    signUpFree: "Inscription gratuite",
  },
  results: {
    eyebrow: "Pourquoi Bookers ?",
    title: "Des résultats prouvés pour des restaurants comme le vôtre",
    description:
      "Des centaines de restaurants nous font confiance. Bookers transforme vos opérations en salle avec des résultats mesurables.",
  },
  testimonials: {
    eyebrow: "Témoignages",
    title: "Ce que disent nos partenaires",
  },
  stats: {
    marquee: "Plébiscité par des restaurants et hôtels de premier plan",
  },
  support: {
    talkToTeam: "Parler à notre équipe",
  },
  faq: {
    title: "Questions fréquentes",
    description:
      "Tout ce qu'il faut savoir sur Bookers. Encore des questions ? Contactez-nous à tout moment.",
  },
  cta: {
    eyebrow: "Commencer",
    title: "Prêt à remplir plus de tables ?",
    description:
      "Nous vous aidons à simplifier réservations et relation client — mise en place en quelques minutes.",
    signUpFree: "Inscription gratuite",
    bookDemo: "Réserver une démo",
    footnote:
      "Sans carte bancaire. Gratuit jusqu'à 150 réservations / mois pour les nouveaux établissements. Payez uniquement quand vous grandissez.",
  },
  freeTrial: {
    noCard: "Sans carte bancaire. Payez uniquement quand vous grandissez.",
  },
  pricing: {
    eyebrow: "Offres tarifaires",
    title: "Des tarifs simples et transparents",
    description:
      "Tarifs de lancement — choisissez l'offre adaptée à votre établissement. Sans frais cachés, résiliable à tout moment.",
    footer:
      "Tarifs de lancement, facturation mensuelle. TVA non comprise. Événements seuls ou sur mesure ? Bookers est modulaire — contactez-nous.",
    perMonth: "/ mois",
    signUp: "S'inscrire",
  },
  solutions: {
    eyebrow: "Solutions",
    title: "Des solutions de réservation intelligentes pour l'hôtellerie-restauration",
    description:
      "Renforcez restaurant et hôtel avec réservations fluides, service personnalisé et automatisation puissante.",
  },
  problemSolution: {
    title: "Nous vous accompagnons, quel que soit votre établissement",
    problemLabel: "Le problème aujourd'hui",
    solutionLabel: "Avec Bookers",
    learnMore: "En savoir plus",
  },
  footer: {
    tagline:
      "Système de réservation intelligent pour restaurants et hôtellerie. Simplifiez. Gérez. Grandissez.",
    product: "Produit",
    company: "Entreprise",
    resources: "Ressources",
    features: "Produit",
    solutions: "Solutions",
    pricing: "Tarifs",
    integrations: "Intégrations",
    ownerApp: "App propriétaire",
    about: "À propos",
    blog: "Blog",
    contact: "Contact",
    bookDemo: "Réserver une démo",
    partners: "Partenaires",
    helpCenter: "Centre d'aide",
    apiDocs: "Documentation API",
    status: "Statut",
    community: "Communauté",
    webinars: "Webinaires",
    rights: "© 2026 Bookers. Tous droits réservés.",
    terms: "Conditions",
    privacy: "Confidentialité",
    cookies: "Cookies",
    madeFor: "Conçu pour l'hôtellerie-restauration",
  },
  contact: {
    eyebrow: "Contact",
    title: "Parlons de votre projet",
    subtitle:
      "Remplissez le formulaire ci-dessous et notre équipe vous recontactera dans les plus brefs délais.",
    blurb:
      "Simplifiez la gestion de vos réservations avec notre système intelligent pour la restauration et l'hôtellerie.",
    address: "Adresse",
    addressValue: "Neuchâtel, Suisse",
    phone: "Téléphone",
    email: "Email",
    supportHours: "Horaires de support",
    supportLine: "24/7 — Nous parlons FR, DE, IT, EN",
    lastName: "Nom",
    firstName: "Prénom",
    restaurant: "Nom du restaurant",
    message: "Message",
    required: "Champs obligatoires",
    placeholderLast: "Dupont",
    placeholderFirst: "Marie",
    placeholderRestaurant: "Le Petit Bistro",
    placeholderPhone: "+41 00 000 00 00",
    placeholderMessage: "Comment pouvons-nous vous aider ?",
    submit: "Envoyer le message",
    sending: "Envoi en cours...",
    successTitle: "Message envoyé !",
    successBody: "Merci {name}, notre équipe vous recontactera dans les plus brefs délais.",
    backHome: "Retour à l'accueil",
    toastRequired: "Veuillez remplir les champs obligatoires.",
    toastSuccess: "Message envoyé avec succès !",
    toastError: "Erreur lors de l'envoi. Veuillez réessayer.",
  },
  signup: {
    panelTitle: "Simplifiez vos réservations, dès aujourd'hui.",
    panelSub: "Rejoignez des centaines de restaurants qui utilisent Bookers pour remplir leurs tables.",
    rated: "Noté 4,9/5 par nos partenaires",
    imageAlt: "Intérieur de restaurant",
    eyebrow: "Réserver une démo",
    title: "Commencez avec Bookers",
    subtitle:
      "Remplissez le formulaire et notre équipe vous contactera pour organiser une démo personnalisée.",
    restaurant: "Nom du restaurant",
    lastName: "Nom",
    firstName: "Prénom",
    phone: "Téléphone",
    address: "Adresse",
    message: "Message",
    placeholderRestaurant: "Le Petit Bistro",
    placeholderLast: "Dupont",
    placeholderFirst: "Marie",
    placeholderPhone: "+41 00 000 00 00",
    placeholderAddress: "Rue de la Gare 12, 2000 Neuchâtel",
    placeholderMessage: "Parlez-nous de votre établissement...",
    consentPrefix:
      "En soumettant ce formulaire, vous acceptez que Bookers traite vos données dans le cadre de son intérêt légitime et pour vous envoyer des communications marketing. Vous pouvez vous désinscrire à tout moment. Tous nos emails contiennent un lien de désinscription. Consultez notre ",
    privacyLink: "politique de confidentialité",
    consentSuffix: ".",
    submit: "Réserver une démo",
    sending: "Envoi en cours...",
    footnote: "Gratuit jusqu'à 150 réservations / mois. Sans carte bancaire.",
    successTitle: "Demande envoyée !",
    successBody: "Merci {name}, notre équipe vous contactera très prochainement pour organiser votre démo.",
    backHome: "Retour à l'accueil",
    toastRequired: "Veuillez remplir tous les champs obligatoires.",
    toastConsent: "Veuillez accepter les conditions.",
    toastSuccess: "Demande de démo envoyée !",
    toastError: "Erreur lors de l'envoi. Veuillez réessayer.",
  },
  legal: {
    back: "← Retour",
    loading: "Chargement...",
    empty: "Contenu en cours de publication.",
    contactLine: "Pour toute question, contactez-nous à",
    termsTitle: "Conditions générales d'utilisation",
    privacyTitle: "Politique de confidentialité",
    cookiesTitle: "Politique de cookies",
    lastUpdated: "Dernière mise à jour : {date}",
  },
  floating: {
    aria: "Demander un rappel",
    title: "Être rappelé",
    subtitle: "Laissez vos coordonnées, nous vous rappelons rapidement.",
    namePlaceholder: "Votre nom",
    submit: "Demander un rappel",
    successTitle: "Demande envoyée !",
    successBody: "Nous vous rappellerons bientôt.",
    close: "Fermer",
    toastRequired: "Veuillez remplir les deux champs.",
    toastSuccess: "Nous vous rappellerons bientôt !",
    toastError: "Erreur. Veuillez réessayer.",
  },
  common: {
    chf: "CHF",
  },
  errors: {
    generic: "Une erreur est survenue. Veuillez réessayer.",
    not_authenticated: "Non authentifié",
    invalid_token_type: "Type de jeton invalide",
    user_not_found: "Utilisateur introuvable",
    admin_required: "Accès administrateur requis",
    token_expired: "Session expirée. Veuillez vous reconnecter.",
    invalid_token: "Session invalide",
    invalid_email_or_password: "Email ou mot de passe incorrect",
    current_password_incorrect: "Mot de passe actuel incorrect",
    password_must_differ: "Le nouveau mot de passe doit être différent de l'actuel",
    password_too_short: "Le mot de passe doit contenir au moins {min} caractères",
    lead_not_found: "Lead introuvable",
    login_failed: "Échec de la connexion",
  },
  admin: {
    title: "Panneau d'administration",
    loginSubtitle: "Connectez-vous pour gérer votre site",
    email: "Email",
    password: "Mot de passe",
    signIn: "Se connecter",
    save: "Sauvegarder",
    logout: "Déconnexion",
    leads: "Leads",
    cms: "CMS",
    passwordTab: "Mot de passe",
    statusUpdated: "Statut mis à jour",
    leadDeleted: "Lead supprimé",
    deleteConfirm: "Supprimer ce lead ?",
    error: "Erreur",
    noLeads: "Aucun lead",
    total: "Total",
    newLeads: "Nouveaux",
    contacts: "Contacts",
    demos: "Démos",
    callbacks: "Rappels",
    filterAll: "Tous",
    sectionSaved: "Section sauvegardée !",
    saveError: "Erreur de sauvegarde",
    passwordUpdated: "Mot de passe mis à jour",
  },
};

// CMS / mock fallbacks — parallel arrays aligned with mock.js order
const contentEn = {
  navFeatures: [
    {
      group: "AI & Automation",
      items: [
        { title: "AI Virtual Host", desc: "Handles queries, menus & orders automatically." },
        { title: "Smart Upselling", desc: "Suggests add-ons and upgrades to boost sales." },
        { title: "Auto Confirmations", desc: "Sends instant SMS and email booking alerts." },
        { title: "Predictive Analytics", desc: "Tracks trends and guest behavior insights." },
      ],
    },
    {
      group: "Reservation Tools",
      items: [
        { title: "24/7 Online Booking", desc: "Guests can reserve anytime, from anywhere." },
        { title: "Real-Time Availability", desc: "Live table updates for efficient seating." },
        { title: "Visual Table Planner", desc: "Drag-and-drop tool for easy layout control." },
        { title: "Waitlist Management", desc: "Notify guests via SMS when tables are ready." },
      ],
    },
    {
      group: "Operations & Integrations",
      items: [
        { title: "Event Management", desc: "Sell and manage pre-paid dining events." },
        { title: "POS Integration", desc: "Sync bookings with ChasPOS and other systems." },
        { title: "Owner App", desc: "Manage capacity and bookings from your mobile." },
        { title: "CRM & Reporting", desc: "Access insights to grow guest engagement." },
      ],
    },
  ],
  topStats: [
    { tag: "Faster", value: "3x", suffix: "", note: "more online bookings through automation." },
    { tag: "Smarter", value: "40", suffix: "%", note: "fewer no-shows via reminders." },
    { tag: "Leaner", value: "65", suffix: "%", note: "less time spent on the phone." },
  ],
  services: [
    {
      title: "Book a Table",
      desc: "Offer seamless online reservations with real-time availability, automated confirmations, and smart table management.",
    },
    {
      title: "Experiences",
      desc: "From curated tastings to chef-driven journeys — discover unforgettable moments your guests will remember.",
    },
    {
      title: "Events",
      desc: "Plan your next gathering with us — intimate, stylish, and perfectly organized from invite to last course.",
    },
  ],
  features: [
    { title: "24/7 Booking", desc: "Guests can book tables anytime with 24/7 online access." },
    { title: "Real-Time Availability", desc: "See real-time table availability to manage seating efficiently." },
    { title: "Visual Table Planner", desc: "Drag and drop tables easily with the visual planner tool." },
    { title: "Auto Confirmations", desc: "Automated emails and SMS reduce no-shows dramatically." },
    { title: "Guest Profiles", desc: "Store guest preferences and history for personalized service." },
    { title: "Secure Payments", desc: "Securely collect deposits and payments before reservations." },
    { title: "Analytics & CRM", desc: "Reports help improve guest experience and sales." },
    { title: "System Integrations", desc: "Integrates smoothly with POS, PMS, and other key systems." },
  ],
  modules: [
    { label: "Tables", desc: "Reservations & seating" },
    { label: "Experiences", desc: "Tastings & chef menus" },
    { label: "Events", desc: "Ticketed & prepaid events" },
  ],
  paymentControls: [
    { title: "Penalize no-shows", desc: "Automatically charge or flag guests who do not show up — protect every cover." },
    { title: "Deposits for tables", desc: "Set refundable deposits on table reservations to secure every booking." },
    { title: "Advance payments for events", desc: "Collect advance payments and ticket fees for events, upfront and automatically." },
  ],
  steps: [
    { title: "Sign up", desc: "Create your Bookers account in minutes — no setup fees and no long-term contracts." },
    { title: "Download the app", desc: "Get Bookers on iOS or Google Play and start receiving reservations instantly." },
    {
      title: "Embed or share",
      desc: "Embed the reservation system directly inside your website, or share your dedicated your-store.wearebookers.com link on social media, Google Business, or by email.",
    },
    { title: "Manage on the go", desc: "Take reservations directly on your mobile phone through the app — no hassle, wherever you are." },
  ],
  results: [
    { value: "3", suffix: "x", note: "more online bookings through automated tools." },
    { value: "40", suffix: "%", note: "fewer no-shows via confirmations and reminders." },
    { value: "65", suffix: "%", note: "less time spent on the phone." },
    { value: "98", suffix: "%", note: "user satisfaction from restaurant partners." },
  ],
  freeTrial: {
    eyebrow: "Starting a new business?",
    title: "Just launching? Bookers is free to begin.",
    text: "If you are opening a new venue, use Bookers completely free until you reach 150 reservations per month. No credit card — pay only when you grow.",
    highlightValue: "150",
    highlightLabel: "free reservations / month",
    cta: "Start for free",
  },
  support: {
    eyebrow: "Support",
    title: "24/7 support from Neuchâtel",
    text: "Real people, based in Switzerland. Our team is here around the clock — and we speak your language.",
    languages: ["Français", "Deutsch", "Italiano", "English"],
    stats: [
      { value: "24/7", note: "Support availability" },
      { value: "4", note: "Languages spoken" },
      { value: "<5 min", note: "Average response time" },
    ],
  },
  faqs: [
    {
      q: "What is Bookers and what can I use it for?",
      a: "Bookers is a cloud-based smart reservation system for restaurants and hospitality venues. It lets you centrally manage tables, experiences, events, guests, payments and reporting — all from one platform.",
    },
    {
      q: "Is Bookers free?",
      a: "Yes, 100% free. No credit card required. New venues get up to 150 free reservations per month — you only pay when you grow.",
    },
    {
      q: "Which businesses is Bookers suited for?",
      a: "Bookers is ideal for restaurants, cafes, bars, hotels and hospitality groups of any size — from a single venue to multi-location operations.",
    },
    {
      q: "Do I need technical knowledge to use Bookers?",
      a: "No. Bookers is built so that anyone on your team can manage bookings and tables efficiently, without any technical or setup expertise.",
    },
    {
      q: "Is Bookers cloud-based?",
      a: "Yes. You can access Bookers anytime and from anywhere through your browser or the mobile owner app.",
    },
    {
      q: "Does Bookers integrate with my POS?",
      a: "Yes. Bookers syncs smoothly with ChasPOS and other major POS and PMS systems so your bookings and floor stay in sync.",
    },
    {
      q: "Can I manage multiple venues in one account?",
      a: "Absolutely. Bookers lets you manage several restaurants or locations within a single account and dashboard.",
    },
  ],
  packages: [
    {
      name: "Tables",
      desc: "Everything you need to take table reservations.",
      tag: "Introduction price",
      offer: "Launch offer",
      features: [
        "Dedicated your-store.wearebookers.com page",
        "Embed the reservation system on your website",
        "24/7 online reservations",
        "Real-time availability & table planner",
        "Automated confirmations & reminders",
        "Deposits & no-show protection",
        "iOS + Android app",
        "Reporting & analytics",
      ],
    },
    {
      name: "Tables + Experiences",
      desc: "Add curated experiences, tastings and chef menus.",
      tag: "Most popular",
      offer: "Launch offer",
      features: [
        "Everything in Tables",
        "Curated experiences & tastings",
        "Prepaid experience bookings",
        "Smart upselling & add-ons",
        "Advanced CRM & guest profiles",
      ],
    },
    {
      name: "Full Suite",
      desc: "Tables, experiences and full event management.",
      tag: "Introduction price",
      offer: "Launch offer",
      features: [
        "Everything in Experiences",
        "Full event management",
        "Advance payments for events",
        "Ticketing & capacity control",
        "Priority onboarding & support",
      ],
    },
  ],
  solutions: [
    {
      title: "For Restaurants",
      lead: "Simplify booking processes, enhance guest satisfaction, and optimize table management.",
      points: [
        { title: "Effortless Booking Management", desc: "24/7 online reservations with real-time availability and a visual seating planner to maximize occupancy." },
        { title: "Reduce No-Shows", desc: "Automated confirmations and reminders, plus payment integration to secure bookings." },
        { title: "Personalized Guest Experience", desc: "Store guest preferences, track visit history, and engage diners with targeted service." },
      ],
    },
    {
      title: "For Hotels",
      lead: "Manage room reservations, events, and guest services all in one place.",
      points: [
        { title: "Event Bookings", desc: "Synchronize room and event reservations with an easy-to-use dashboard and real-time inventory." },
        { title: "Increase Revenue with Upselling", desc: "Offer pre-paid services, add-ons, and special packages to maximize guest spending." },
        { title: "Guest Engagement & Loyalty", desc: "Build relationships through personalized offers, loyalty programs, and automation." },
      ],
    },
  ],
  problemBlocks: [
    {
      label: "Independent Restaurants",
      problem: "Reservations scattered across phone, paper books and DMs. Double-bookings, no-shows and empty tables every week.",
      solution: "One clean dashboard. Guests book in 30 seconds, confirmations go out automatically, and your floor stays full.",
    },
    {
      label: "Hotels & Groups",
      problem: "Rooms, dining and events live in different tools. Staff wait on updates and nobody has the full picture.",
      solution: "Rooms, tables and events synced in real time — with upselling and loyalty built right in to grow revenue.",
    },
  ],
};

const contentFr = {
  navFeatures: [
    {
      group: "IA & automatisation",
      items: [
        { title: "Hôte virtuel IA", desc: "Gère demandes, menus et commandes automatiquement." },
        { title: "Vente additionnelle intelligente", desc: "Suggère des options pour augmenter le chiffre d'affaires." },
        { title: "Confirmations automatiques", desc: "Envoie SMS et e-mails de réservation instantanément." },
        { title: "Analyses prédictives", desc: "Suit tendances et comportement des clients." },
      ],
    },
    {
      group: "Outils de réservation",
      items: [
        { title: "Réservation en ligne 24/7", desc: "Les clients réservent à tout moment, où qu'ils soient." },
        { title: "Disponibilité en temps réel", desc: "Mises à jour live pour un service en salle efficace." },
        { title: "Plan de salle visuel", desc: "Glisser-déposer pour contrôler facilement le plan." },
        { title: "Gestion de liste d'attente", desc: "SMS quand une table est prête." },
      ],
    },
    {
      group: "Opérations & intégrations",
      items: [
        { title: "Gestion d'événements", desc: "Vendez et gérez des événements gastronomiques prépayés." },
        { title: "Intégration caisse", desc: "Synchronisez avec ChasPOS et d'autres systèmes." },
        { title: "App propriétaire", desc: "Gérez capacité et réservations depuis le mobile." },
        { title: "CRM & rapports", desc: "Insights pour fidéliser vos clients." },
      ],
    },
  ],
  topStats: [
    { tag: "Plus rapide", value: "3x", suffix: "", note: "de réservations en ligne grâce à l'automatisation." },
    { tag: "Plus malin", value: "40", suffix: "%", note: "de no-shows en moins via les rappels." },
    { tag: "Plus léger", value: "65", suffix: "%", note: "de temps passé au téléphone en moins." },
  ],
  services: [
    {
      title: "Réserver une table",
      desc: "Réservations en ligne fluides, disponibilité en temps réel, confirmations automatiques et gestion intelligente des tables.",
    },
    {
      title: "Expériences",
      desc: "Dégustations et parcours chef — des moments inoubliables pour vos clients.",
    },
    {
      title: "Événements",
      desc: "Organisez votre prochain événement — intime, élégant, de l'invitation au dernier service.",
    },
  ],
  features: [
    { title: "Réservation 24/7", desc: "Les clients réservent à tout moment en ligne." },
    { title: "Disponibilité en temps réel", desc: "Visualisez les tables pour un service efficace." },
    { title: "Plan de salle visuel", desc: "Glisser-déposer pour organiser votre salle." },
    { title: "Confirmations auto", desc: "E-mails et SMS automatiques pour réduire les no-shows." },
    { title: "Profils clients", desc: "Préférences et historique pour un service personnalisé." },
    { title: "Paiements sécurisés", desc: "Acomptes et paiements avant la réservation." },
    { title: "Analyses & CRM", desc: "Rapports pour améliorer expérience et ventes." },
    { title: "Intégrations", desc: "Synchronisation avec caisse, PMS et outils clés." },
  ],
  modules: [
    { label: "Tables", desc: "Réservations & placement" },
    { label: "Expériences", desc: "Dégustations & menus chef" },
    { label: "Événements", desc: "Événements payants & prépayés" },
  ],
  paymentControls: [
    { title: "Pénaliser les no-shows", desc: "Facturez ou signalez automatiquement les absences — protégez chaque couvert." },
    { title: "Acomptes sur tables", desc: "Acomptes remboursables pour sécuriser chaque réservation." },
    { title: "Paiements anticipés événements", desc: "Encaissez billets et acomptes d'événements automatiquement." },
  ],
  steps: [
    { title: "Inscription", desc: "Créez votre compte Bookers en minutes — sans frais d'installation ni engagement long." },
    { title: "Téléchargez l'app", desc: "Bookers sur iOS ou Google Play et recevez des réservations instantanément." },
    {
      title: "Intégrez ou partagez",
      desc: "Intégrez le système sur votre site ou partagez votre lien your-store.wearebookers.com sur les réseaux, Google Business ou par e-mail.",
    },
    { title: "Gérez en mobilité", desc: "Prenez des réservations sur votre téléphone — partout, sans friction." },
  ],
  results: [
    { value: "3", suffix: "x", note: "de réservations en ligne via l'automatisation." },
    { value: "40", suffix: "%", note: "de no-shows en moins grâce aux confirmations." },
    { value: "65", suffix: "%", note: "de temps au téléphone en moins." },
    { value: "98", suffix: "%", note: "de satisfaction chez nos partenaires." },
  ],
  freeTrial: {
    eyebrow: "Vous lancez un établissement ?",
    title: "Vous débutez ? Bookers est gratuit pour commencer.",
    text: "Pour une nouvelle adresse, Bookers est gratuit jusqu'à 150 réservations par mois. Sans carte bancaire — payez uniquement quand vous grandissez.",
    highlightValue: "150",
    highlightLabel: "réservations gratuites / mois",
    cta: "Commencer gratuitement",
  },
  support: {
    eyebrow: "Support",
    title: "Support 24/7 depuis Neuchâtel",
    text: "De vraies personnes, en Suisse. Notre équipe est disponible en continu — dans votre langue.",
    languages: ["Français", "Deutsch", "Italiano", "English"],
    stats: [
      { value: "24/7", note: "Disponibilité support" },
      { value: "4", note: "Langues parlées" },
      { value: "<5 min", note: "Temps de réponse moyen" },
    ],
  },
  faqs: [
    {
      q: "Qu'est-ce que Bookers et à quoi sert-il ?",
      a: "Bookers est un système de réservation cloud pour restaurants et hôtellerie. Gérez tables, expériences, événements, clients, paiements et rapports depuis une seule plateforme.",
    },
    {
      q: "Bookers est-il gratuit ?",
      a: "Oui, 100 % gratuit. Sans carte bancaire. Jusqu'à 150 réservations gratuites par mois pour les nouveaux établissements — vous payez uniquement quand vous grandissez.",
    },
    {
      q: "Pour quels établissements Bookers est-il adapté ?",
      a: "Idéal pour restaurants, cafés, bars, hôtels et groupes — d'un seul site à plusieurs établissements.",
    },
    {
      q: "Faut-il des compétences techniques ?",
      a: "Non. Toute votre équipe peut gérer réservations et tables sans expertise technique.",
    },
    {
      q: "Bookers est-il dans le cloud ?",
      a: "Oui. Accédez-y via navigateur ou l'app mobile propriétaire, à tout moment.",
    },
    {
      q: "Bookers s'intègre-t-il à ma caisse ?",
      a: "Oui. Synchronisation avec ChasPOS et les principaux POS/PMS pour garder salle et réservations alignées.",
    },
    {
      q: "Puis-je gérer plusieurs établissements ?",
      a: "Absolument. Plusieurs restaurants ou sites dans un même compte et tableau de bord.",
    },
  ],
  packages: [
    {
      name: "Tables",
      desc: "Tout pour prendre des réservations de tables.",
      tag: "Prix de lancement",
      offer: "Offre de lancement",
      features: [
        "Page dédiée your-store.wearebookers.com",
        "Intégration sur votre site web",
        "Réservations en ligne 24/7",
        "Disponibilité & plan de salle",
        "Confirmations & rappels automatiques",
        "Acomptes & protection no-show",
        "App iOS + Android",
        "Rapports & analyses",
      ],
    },
    {
      name: "Tables + Expériences",
      desc: "Expériences, dégustations et menus chef.",
      tag: "Le plus populaire",
      offer: "Offre de lancement",
      features: [
        "Tout dans Tables",
        "Expériences & dégustations",
        "Réservations d'expériences prépayées",
        "Vente additionnelle intelligente",
        "CRM avancé & profils clients",
      ],
    },
    {
      name: "Suite complète",
      desc: "Tables, expériences et gestion complète d'événements.",
      tag: "Prix de lancement",
      offer: "Offre de lancement",
      features: [
        "Tout dans Expériences",
        "Gestion complète d'événements",
        "Paiements anticipés événements",
        "Billetterie & contrôle de capacité",
        "Onboarding & support prioritaires",
      ],
    },
  ],
  solutions: [
    {
      title: "Pour les restaurants",
      lead: "Simplifiez les réservations, améliorez la satisfaction client et optimisez la gestion des tables.",
      points: [
        { title: "Gestion des réservations simplifiée", desc: "Réservations 24/7, disponibilité en temps réel et plan de salle pour maximiser l'occupation." },
        { title: "Réduire les no-shows", desc: "Confirmations et rappels automatiques, plus intégration paiement." },
        { title: "Expérience client personnalisée", desc: "Préférences, historique et service ciblé." },
      ],
    },
    {
      title: "Pour les hôtels",
      lead: "Chambres, événements et services clients au même endroit.",
      points: [
        { title: "Réservations d'événements", desc: "Synchronisez chambres et événements avec un tableau de bord simple." },
        { title: "Revenus via vente additionnelle", desc: "Services prépayés, options et forfaits pour maximiser les dépenses." },
        { title: "Fidélisation", desc: "Offres personnalisées, programmes fidélité et automatisation." },
      ],
    },
  ],
  problemBlocks: [
    {
      label: "Restaurants indépendants",
      problem: "Réservations éparpillées entre téléphone, carnets et messages. Doubles réservations, no-shows et tables vides chaque semaine.",
      solution: "Un tableau de bord clair. Les clients réservent en 30 secondes, confirmations automatiques, salle remplie.",
    },
    {
      label: "Hôtels & groupes",
      problem: "Chambres, restauration et événements dans des outils séparés. L'équipe attend des mises à jour, personne n'a la vue complète.",
      solution: "Chambres, tables et événements synchronisés en temps réel — vente additionnelle et fidélité intégrées.",
    },
  ],
};

en.content = contentEn;
fr.content = contentFr;

export const translations = { en, fr };

function interpolate(str, vars) {
  if (typeof str !== "string") return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : `{${k}}`));
}

export function get(locale, path, fallback = "", vars = {}) {
  const parts = path.split(".");
  let cur = translations[locale] || translations.en;
  for (const p of parts) {
    cur = cur && cur[p];
    if (cur == null) break;
  }
  if (cur == null) {
    let f = translations.en;
    for (const p of parts) {
      f = f && f[p];
      if (f == null) break;
    }
    cur = f ?? fallback;
  }
  if (typeof cur === "string") return interpolate(cur, vars);
  return cur ?? fallback;
}
